import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, Heading, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from '@chakra-ui/react';
import { getSuperMerchantBeneficiaries } from 'api/beneficiaries';
import { BENEFICIARIES_ROUTE } from 'constants/routes';
import BeneficiaryList from 'modules/beneficiaries/components/BeneficiaryList';
import { BENEFICIARY_DEFAULT_PAGE_SIZE, BENEFICIARY_TABS } from 'modules/beneficiaries/constants/constants';

const INITIAL_TAB_STATE = BENEFICIARY_TABS.reduce((acc, _tab, index) => ({ ...acc, [index]: null }), {});
const INITIAL_QUERY_STATE = BENEFICIARY_TABS.reduce((acc, _tab, index) => ({ ...acc, [index]: { page: 1 } }), {});

const SuperMerchantBeneficiaries = () => {
  const history = useHistory();
  const location = useLocation();
  const { superMerchantId } = useParams();

  const queryParams = new URLSearchParams(location.search);
  const initialTab = Number(queryParams.get('tab')) || 0;

  const superMerchantName = location?.state?.superMerchantName ?? superMerchantId;

  const [activeTab, setActiveTab] = useState(initialTab);
  const [tabData, setTabData] = useState(INITIAL_TAB_STATE);
  const [tabLoading, setTabLoading] = useState({});
  const [tabError, setTabError] = useState({});
  const [tabQueries, setTabQueries] = useState(INITIAL_QUERY_STATE);
  const [totalPages, setTotalPages] = useState(INITIAL_TAB_STATE);
  const [totalCounts, setTotalCounts] = useState(INITIAL_TAB_STATE);

  const latestRequestRef = useRef(0);

  const fetchTabData = useCallback(
    async (tabIndex, page, searchTerm) => {
      const tab = BENEFICIARY_TABS[tabIndex];

      if (!tab || !superMerchantId) return;

      latestRequestRef.current += 1;
      const requestId = latestRequestRef.current;

      setTabLoading((prev) => ({ ...prev, [tabIndex]: true }));
      setTabError((prev) => ({ ...prev, [tabIndex]: null }));

      try {
        const response = await getSuperMerchantBeneficiaries(superMerchantId, {
          status: tab.statuses.join(','),
          page,
          pageSize: BENEFICIARY_DEFAULT_PAGE_SIZE,
          searchParams: searchTerm,
        });

        if (requestId !== latestRequestRef.current) return;

        const data = response?.data?.data;

        setTabData((prev) => ({ ...prev, [tabIndex]: data?.records ?? [] }));
        setTotalPages((prev) => ({ ...prev, [tabIndex]: data?.totalPages ?? 0 }));
        setTotalCounts((prev) => ({ ...prev, [tabIndex]: data?.totalRecords ?? 0 }));
      } catch (err) {
        if (requestId !== latestRequestRef.current) return;

        setTabError((prev) => ({ ...prev, [tabIndex]: err }));
        setTabData((prev) => ({ ...prev, [tabIndex]: [] }));
      } finally {
        if (requestId === latestRequestRef.current) {
          setTabLoading((prev) => ({ ...prev, [tabIndex]: false }));
        }
      }
    },
    [superMerchantId]
  );

  const activeQuery = tabQueries[activeTab];

  useEffect(() => {
    fetchTabData(activeTab, activeQuery?.page, activeQuery?.search);
  }, [fetchTabData, activeTab, activeQuery]);

  const handleTabChange = (tabIndex) => {
    setActiveTab(tabIndex);
    history.replace(`${location.pathname}?tab=${tabIndex}`, location.state);
  };

  const handlePageChange = (tabIndex, page) => {
    setTabQueries((prev) => ({ ...prev, [tabIndex]: { ...prev[tabIndex], page } }));
  };

  const handleSearch = (tabIndex, searchTerm) => {
    setTabQueries((prev) => {
      const current = prev[tabIndex] ?? { page: 1 };

      if (current.search === searchTerm) return prev;

      return { ...prev, [tabIndex]: { page: 1, search: searchTerm } };
    });
  };

  return (
    <Box p={4}>
      <Flex align='center' justify='space-between' mb={2}>
        <Box>
          <Heading as='h1' size='md' color='gray.700'>
            {superMerchantName}
          </Heading>
          <Text fontSize='sm' color='gray.500'>
            {superMerchantId}
          </Text>
        </Box>
        <Button
          leftIcon={<ArrowBackIcon />}
          size='sm'
          variant='outline'
          onClick={() => history.push(BENEFICIARIES_ROUTE)}
        >
          All Super Merchants
        </Button>
      </Flex>

      <Tabs index={activeTab} onChange={handleTabChange} isLazy>
        <TabList>
          {BENEFICIARY_TABS.map((tab) => (
            <Tab key={tab.title}>{tab.title}</Tab>
          ))}
        </TabList>
        <TabPanels>
          {BENEFICIARY_TABS.map((tab, tabIndex) => (
            <TabPanel key={tab.title} px={0}>
              <BeneficiaryList
                title={tab.title}
                beneficiaries={tabData[tabIndex] ?? []}
                isLoading={!!tabLoading[tabIndex]}
                error={tabError[tabIndex]}
                currentPage={tabQueries[tabIndex]?.page ?? 1}
                totalPages={totalPages[tabIndex] ?? 0}
                totalCount={totalCounts[tabIndex] ?? 0}
                tab={tabIndex}
                onPageChange={(page) => handlePageChange(tabIndex, page)}
                onSearch={(searchTerm) => handleSearch(tabIndex, searchTerm)}
              />
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default SuperMerchantBeneficiaries;
